/**
 * CsvMappingStep — Schritt 3 des Import-Wizards (nur CSV ohne auto-erkanntes
 * Profil, architecture-bank-reconciliation.md §4 Schritt 5 / §5.2).
 *
 * Reines Präsentations-/Formular-Modul: baut aus User-Eingaben eine
 * `CsvProfileShape`, ruft `parseBankCsv` testweise auf (Fehler bleiben lokal,
 * blockieren den Schritt) und übergibt bei Erfolg das fertige Profil +
 * Parse-Ergebnis + Profilnamen an den Elternteil (`ImportWizard`), der das
 * Speichern (`saveCsvProfile`) übernimmt (keine eigene Firestore-Anbindung
 * hier — Testbarkeit, ein Datenpfad).
 *
 * @module CsvMappingStep
 */

import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { parseBankCsv } from '../../utils/bankImport/parseBankCsv';
import type { CsvProfileShape, ParsedStatement } from '../../utils/bankImport/types';
import type { BankCsvColumnMap } from '../../types/bankTypes';
import logger from '../../utils/logger';
import { mapErrorToUserMessage } from '../../utils/userErrorMessage';

type Delimiter = ';' | ',' | '\t';
type Encoding = 'utf-8' | 'windows-1252';
type DecimalStyle = 'de' | 'en';
type DateFormat = 'DD.MM.YYYY' | 'DD.MM.YY' | 'YYYY-MM-DD' | 'MM/DD/YYYY';
type AmountMode = 'amount' | 'amountWithSign' | 'debitCredit';

export interface CsvMappingStepProps {
  /** Bereits dekodierter Text der Datei (Auto-Encoding-Versuch aus Schritt 1). */
  initialText: string;
  /** Roh-Bytes — wird bei Encoding-Wechsel neu dekodiert (siehe `onRedecode`). */
  onRedecode: (encoding: Encoding) => string;
  /** Vorschlag für den Profilnamen (z.B. aus dem Dateinamen). */
  suggestedProfileName: string;
  onCancel: () => void;
  /**
   * May be async (e.g. persists the profile via `saveCsvProfile`). If it
   * rejects, the rejection reason is shown inline on this step (the caller
   * does NOT need its own error UI for this call).
   */
  onConfirm: (args: {
    profile: CsvProfileShape;
    parsed: ParsedStatement;
    profileName: string;
    headerLine: string;
  }) => void | Promise<void>;
}

/** Naiver, quote-toleranter Split NUR zum Befüllen der Spalten-Auswahl (echtes Parsing macht `parseBankCsv`). */
function splitHeaderLine(line: string, delimiter: string): string[] {
  return line
    .split(delimiter)
    .map((cell) => cell.trim().replace(/^"(.*)"$/, '$1').trim());
}

/** Grobe Delimiter-Heuristik für die Default-Vorbelegung. */
function guessDelimiter(firstLine: string): Delimiter {
  const counts: Array<[Delimiter, number]> = [
    [';', (firstLine.match(/;/g) ?? []).length],
    [',', (firstLine.match(/,/g) ?? []).length],
    ['\t', (firstLine.match(/\t/g) ?? []).length],
  ];
  counts.sort((a, b) => b[1] - a[1]);
  return counts[0][1] > 0 ? counts[0][0] : ';';
}

const CsvMappingStep: React.FC<CsvMappingStepProps> = ({
  initialText,
  onRedecode,
  suggestedProfileName,
  onCancel,
  onConfirm,
}) => {
  const { t } = useTranslation('bank');

  const firstLineGuess = (initialText.split(/\r?\n/, 1)[0] ?? '').trim();
  const [text, setText] = useState(initialText);
  const [delimiter, setDelimiter] = useState<Delimiter>(guessDelimiter(firstLineGuess));
  const [encoding, setEncoding] = useState<Encoding>('utf-8');
  const [decimalStyle, setDecimalStyle] = useState<DecimalStyle>('de');
  const [dateFormat, setDateFormat] = useState<DateFormat>('DD.MM.YYYY');
  const [skipLeadingRows, setSkipLeadingRows] = useState(0);
  const [amountMode, setAmountMode] = useState<AmountMode>('amount');

  const [bookingDateCol, setBookingDateCol] = useState('');
  const [valueDateCol, setValueDateCol] = useState('');
  const [amountCol, setAmountCol] = useState('');
  const [signCol, setSignCol] = useState('');
  const [debitValuesRaw, setDebitValuesRaw] = useState('S,Soll');
  const [debitCol, setDebitCol] = useState('');
  const [creditCol, setCreditCol] = useState('');
  const [counterpartyNameCol, setCounterpartyNameCol] = useState('');
  const [counterpartyIbanCol, setCounterpartyIbanCol] = useState('');
  const [purposeCols, setPurposeCols] = useState<string[]>([]);
  const [acctSvcrRefCol, setAcctSvcrRefCol] = useState('');
  const [endToEndIdCol, setEndToEndIdCol] = useState('');
  const [mandateRefCol, setMandateRefCol] = useState('');
  const [bankTxCodeCol, setBankTxCodeCol] = useState('');

  const [profileName, setProfileName] = useState(suggestedProfileName);
  const [error, setError] = useState<string | null>(null);

  const headerLine = useMemo(() => {
    const lines = text.split(/\r?\n/);
    return (lines[skipLeadingRows] ?? '').trim();
  }, [text, skipLeadingRows]);

  const headerColumns = useMemo(
    () => splitHeaderLine(headerLine, delimiter).filter((c) => c.length > 0),
    [headerLine, delimiter]
  );

  const handleEncodingChange = (next: Encoding) => {
    setEncoding(next);
    setText(onRedecode(next));
  };

  const togglePurposeCol = (col: string) => {
    setPurposeCols((prev) => (prev.includes(col) ? prev.filter((c) => c !== col) : [...prev, col]));
  };

  const buildColumnMap = (): BankCsvColumnMap => {
    const columnMap: BankCsvColumnMap = {
      bookingDate: bookingDateCol,
      valueDate: valueDateCol || undefined,
      counterpartyName: counterpartyNameCol || undefined,
      counterpartyIban: counterpartyIbanCol || undefined,
      purpose: purposeCols,
      acctSvcrRef: acctSvcrRefCol || undefined,
      endToEndId: endToEndIdCol || undefined,
      mandateRef: mandateRefCol || undefined,
      bankTxCode: bankTxCodeCol || undefined,
    };
    if (amountMode === 'amount') {
      columnMap.amount = amountCol;
    } else if (amountMode === 'amountWithSign') {
      columnMap.amountWithSign = {
        amount: amountCol,
        signColumn: signCol,
        debitValues: debitValuesRaw.split(',').map((v) => v.trim()).filter((v) => v.length > 0),
      };
    } else {
      columnMap.debitCredit = { debit: debitCol, credit: creditCol };
    }
    return columnMap;
  };

  const validate = (): string | null => {
    if (!bookingDateCol) return t('wizard.csvMapping.errors.bookingDateRequired', 'Bitte die Buchungsdatum-Spalte wählen.');
    if (amountMode === 'amount' && !amountCol) {
      return t('wizard.csvMapping.errors.amountRequired', 'Bitte die Betragsspalte wählen.');
    }
    if (amountMode === 'amountWithSign' && (!amountCol || !signCol)) {
      return t('wizard.csvMapping.errors.amountSignRequired', 'Bitte Betrags- und Vorzeichen-Spalte wählen.');
    }
    if (amountMode === 'debitCredit' && (!debitCol || !creditCol)) {
      return t('wizard.csvMapping.errors.debitCreditRequired', 'Bitte Soll- und Haben-Spalte wählen.');
    }
    if (!profileName.trim()) {
      return t('wizard.csvMapping.errors.nameRequired', 'Bitte einen Profilnamen vergeben.');
    }
    return null;
  };

  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async () => {
    setError(null);
    const validationError = validate();
    if (validationError) {
      setError(validationError);
      return;
    }

    const profile: CsvProfileShape = {
      delimiter,
      encoding,
      decimalStyle,
      dateFormat,
      skipLeadingRows,
      columnMap: buildColumnMap(),
    };

    setSubmitting(true);
    try {
      const parsed = parseBankCsv(text, profile);
      // `onConfirm` may be async (persists the profile) — its rejection is
      // surfaced HERE so the user can retry the mapping without losing input.
      await onConfirm({ profile, parsed, profileName: profileName.trim(), headerLine });
    } catch (err: any) {
      logger.error(err, 'csv mapping parse/confirm failed');
      setError(mapErrorToUserMessage(err, t, { fallbackKey: 'wizard.csvMapping.errors.parseFailed' }));
    } finally {
      setSubmitting(false);
    }
  };

  const selectClass =
    'w-full bg-surface-raised border border-hairline rounded-lg px-3 py-2 text-sm text-primary focus:outline-none focus:border-indigo-500';
  const labelClass = 'block text-xs font-medium text-muted mb-1';

  return (
    <div className="space-y-5">
      <p className="text-sm text-muted">
        {t(
          'wizard.csvMapping.intro',
          'Diese CSV-Datei wurde nicht automatisch erkannt. Ordne die Spalten einmalig zu — das Profil wird für künftige Importe gespeichert.'
        )}
      </p>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div>
          <label className={labelClass}>{t('wizard.csvMapping.delimiter', 'Trennzeichen')}</label>
          <select className={selectClass} value={delimiter} onChange={(e) => setDelimiter(e.target.value as Delimiter)}>
            <option value=";">;</option>
            <option value=",">,</option>
            <option value="\t">Tab</option>
          </select>
        </div>
        <div>
          <label className={labelClass}>{t('wizard.csvMapping.encoding', 'Zeichenkodierung')}</label>
          <select className={selectClass} value={encoding} onChange={(e) => handleEncodingChange(e.target.value as Encoding)}>
            <option value="utf-8">UTF-8</option>
            <option value="windows-1252">Windows-1252</option>
          </select>
        </div>
        <div>
          <label className={labelClass}>{t('wizard.csvMapping.decimalStyle', 'Zahlenformat')}</label>
          <select className={selectClass} value={decimalStyle} onChange={(e) => setDecimalStyle(e.target.value as DecimalStyle)}>
            <option value="de">1.234,56 (DE)</option>
            <option value="en">1,234.56 (EN)</option>
          </select>
        </div>
        <div>
          <label className={labelClass}>{t('wizard.csvMapping.dateFormat', 'Datumsformat')}</label>
          <select className={selectClass} value={dateFormat} onChange={(e) => setDateFormat(e.target.value as DateFormat)}>
            <option value="DD.MM.YYYY">DD.MM.YYYY</option>
            <option value="DD.MM.YY">DD.MM.YY</option>
            <option value="YYYY-MM-DD">YYYY-MM-DD</option>
            <option value="MM/DD/YYYY">MM/DD/YYYY</option>
          </select>
        </div>
        <div>
          <label className={labelClass}>{t('wizard.csvMapping.skipRows', 'Kopfzeilen überspringen')}</label>
          <input
            type="number"
            min={0}
            className={selectClass}
            value={skipLeadingRows}
            onChange={(e) => setSkipLeadingRows(Math.max(0, parseInt(e.target.value, 10) || 0))}
          />
        </div>
        <div className="sm:col-span-3">
          <label className={labelClass}>{t('wizard.csvMapping.profileName', 'Profilname')}</label>
          <input
            type="text"
            className={selectClass}
            value={profileName}
            onChange={(e) => setProfileName(e.target.value)}
            placeholder={t('wizard.csvMapping.profileNamePlaceholder', 'z.B. Sparkasse KK-Export')}
          />
        </div>
      </div>

      <div className="border-t border-hairline pt-4">
        <p className="text-xs text-faint mb-3">
          {headerColumns.length > 0
            ? t('wizard.csvMapping.columnsFound', '{{count}} Spalten erkannt', { count: headerColumns.length })
            : t('wizard.csvMapping.noColumns', 'Keine Spalten erkannt — Trennzeichen/Kopfzeilen prüfen.')}
        </p>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <ColumnSelect label={t('wizard.csvMapping.field.bookingDate', 'Buchungsdatum *')} value={bookingDateCol} onChange={setBookingDateCol} options={headerColumns} className={selectClass} labelClassName={labelClass} required />
          <ColumnSelect label={t('wizard.csvMapping.field.valueDate', 'Valuta')} value={valueDateCol} onChange={setValueDateCol} options={headerColumns} className={selectClass} labelClassName={labelClass} />
          <ColumnSelect label={t('wizard.csvMapping.field.counterpartyName', 'Gegenpartei')} value={counterpartyNameCol} onChange={setCounterpartyNameCol} options={headerColumns} className={selectClass} labelClassName={labelClass} />
          <ColumnSelect label={t('wizard.csvMapping.field.counterpartyIban', 'Gegenpartei-IBAN')} value={counterpartyIbanCol} onChange={setCounterpartyIbanCol} options={headerColumns} className={selectClass} labelClassName={labelClass} />
          <ColumnSelect label={t('wizard.csvMapping.field.acctSvcrRef', 'Bank-Referenz')} value={acctSvcrRefCol} onChange={setAcctSvcrRefCol} options={headerColumns} className={selectClass} labelClassName={labelClass} />
          <ColumnSelect label={t('wizard.csvMapping.field.endToEndId', 'EndToEndId')} value={endToEndIdCol} onChange={setEndToEndIdCol} options={headerColumns} className={selectClass} labelClassName={labelClass} />
          <ColumnSelect label={t('wizard.csvMapping.field.mandateRef', 'Mandatsreferenz')} value={mandateRefCol} onChange={setMandateRefCol} options={headerColumns} className={selectClass} labelClassName={labelClass} />
          <ColumnSelect label={t('wizard.csvMapping.field.bankTxCode', 'Buchungstext')} value={bankTxCodeCol} onChange={setBankTxCodeCol} options={headerColumns} className={selectClass} labelClassName={labelClass} />
        </div>

        <div className="mt-3">
          <label className={labelClass}>{t('wizard.csvMapping.field.purpose', 'Verwendungszweck (mehrere möglich)')}</label>
          <div className="flex flex-wrap gap-2">
            {headerColumns.map((col) => (
              <label key={col} className="flex items-center gap-1.5 text-xs text-secondary bg-surface-raised border border-hairline rounded-lg px-2 py-1 cursor-pointer">
                <input type="checkbox" checked={purposeCols.includes(col)} onChange={() => togglePurposeCol(col)} />
                {col}
              </label>
            ))}
          </div>
        </div>

        <div className="mt-4">
          <label className={labelClass}>{t('wizard.csvMapping.amountMode', 'Betragsspalte(n)')}</label>
          <div className="flex gap-4 mb-2 text-xs text-secondary">
            <label className="flex items-center gap-1.5 cursor-pointer">
              <input type="radio" checked={amountMode === 'amount'} onChange={() => setAmountMode('amount')} />
              {t('wizard.csvMapping.amountModeSingle', 'Eine signierte Spalte')}
            </label>
            <label className="flex items-center gap-1.5 cursor-pointer">
              <input type="radio" checked={amountMode === 'amountWithSign'} onChange={() => setAmountMode('amountWithSign')} />
              {t('wizard.csvMapping.amountModeSign', 'Betrag + S/H-Kennzeichen')}
            </label>
            <label className="flex items-center gap-1.5 cursor-pointer">
              <input type="radio" checked={amountMode === 'debitCredit'} onChange={() => setAmountMode('debitCredit')} />
              {t('wizard.csvMapping.amountModeSplit', 'Getrennte Soll-/Haben-Spalten')}
            </label>
          </div>

          {amountMode === 'amount' && (
            <ColumnSelect label={t('wizard.csvMapping.field.amount', 'Betrag *')} value={amountCol} onChange={setAmountCol} options={headerColumns} className={selectClass} labelClassName={labelClass} required />
          )}
          {amountMode === 'amountWithSign' && (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <ColumnSelect label={t('wizard.csvMapping.field.amount', 'Betrag *')} value={amountCol} onChange={setAmountCol} options={headerColumns} className={selectClass} labelClassName={labelClass} required />
              <ColumnSelect label={t('wizard.csvMapping.field.signColumn', 'Vorzeichen-Spalte *')} value={signCol} onChange={setSignCol} options={headerColumns} className={selectClass} labelClassName={labelClass} required />
              <div>
                <label className={labelClass}>{t('wizard.csvMapping.field.debitValues', 'Soll-Werte (Komma-getrennt)')}</label>
                <input type="text" className={selectClass} value={debitValuesRaw} onChange={(e) => setDebitValuesRaw(e.target.value)} />
              </div>
            </div>
          )}
          {amountMode === 'debitCredit' && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <ColumnSelect label={t('wizard.csvMapping.field.debitColumn', 'Soll-Spalte *')} value={debitCol} onChange={setDebitCol} options={headerColumns} className={selectClass} labelClassName={labelClass} required />
              <ColumnSelect label={t('wizard.csvMapping.field.creditColumn', 'Haben-Spalte *')} value={creditCol} onChange={setCreditCol} options={headerColumns} className={selectClass} labelClassName={labelClass} required />
            </div>
          )}
        </div>
      </div>

      {error && (
        <div className="bg-[var(--tone-danger-bg)] border border-[var(--tone-danger-border)] rounded-lg p-3 text-sm text-[var(--tone-danger-fg)]">{error}</div>
      )}

      <div className="flex justify-between pt-2">
        <button type="button" onClick={onCancel} disabled={submitting} className="px-4 py-2 text-sm text-muted hover:text-primary disabled:opacity-50">
          {t('wizard.back', 'Zurück')}
        </button>
        <button
          type="button"
          onClick={handleSubmit}
          disabled={submitting}
          className="px-5 py-2 bg-indigo-600 hover:bg-indigo-500 text-white text-sm font-medium rounded-lg disabled:opacity-50"
        >
          {t('wizard.csvMapping.confirm', 'Zuordnung übernehmen')}
        </button>
      </div>
    </div>
  );
};

interface ColumnSelectProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  options: string[];
  className: string;
  labelClassName: string;
  required?: boolean;
}

const ColumnSelect: React.FC<ColumnSelectProps> = ({ label, value, onChange, options, className, labelClassName, required }) => {
  const { t } = useTranslation('bank');
  return (
    <div>
      <label className={labelClassName}>{label}</label>
      <select className={className} value={value} onChange={(e) => onChange(e.target.value)}>
        <option value="">{required ? t('wizard.csvMapping.pleaseSelect', '– wählen –') : t('wizard.csvMapping.none', '– keine –')}</option>
        {options.map((col) => (
          <option key={col} value={col}>
            {col}
          </option>
        ))}
      </select>
    </div>
  );
};

export default CsvMappingStep;
